/**
 * Shared header component for Quiz and Assessment results pages
 */

'use client';

import { ChevronLeftIcon } from '@/components/Icons/Icons';
import CopyLink from '@/components/common/copy-link';
import { formatDate } from '@/helpers/date-format';
import Link from 'next/link';

interface ResultsHeaderProps {
  title: string;
  type: 'quiz' | 'assessment';
  learningPathUrl: string;
  updatedAt: string;
  questionIndex: number;
  totalQuestions: number;
  isStandaloneCourse: boolean;
  /** When true, hide "Question x of y" (score-card-only view on revisit) */
  scoreCardOnly?: boolean;
}

export function ResultsHeader({
  title,
  type,
  learningPathUrl,
  updatedAt,
  questionIndex,
  totalQuestions,
  isStandaloneCourse,
  scoreCardOnly = false,
}: ResultsHeaderProps) {
  const backLinkText = isStandaloneCourse ? 'Back to course' : 'Back to learning path';
  const backLinkHref = isStandaloneCourse 
    ? `/courses${learningPathUrl}` 
    : `/learning-paths${learningPathUrl}`;

  const typeLabel = type === 'assessment' ? 'Assessment' : 'Quiz';
  const titleSuffix = type === 'assessment' ? 'Assessment' : 'Quiz';

  return (
    <div className="bg-surface-3 rounded-lg p-8 flex flex-col gap-[2px] md:rounded-xl">
      <Link
        href={backLinkHref}
        className="additional-type-s-button text-text-action-medium hover:text-text-action-strong flex items-center gap-2 w-fit"
      >
        <ChevronLeftIcon size={12} />
        {backLinkText}
      </Link>

      <div className="flex flex-col gap-1">
        <h1 className="type-h3 lg:h1-compact select-none">
          {title} {titleSuffix}
        </h1>
        <div className="text-text-default-strong select-none flex flex-row gap-2 items-center justify-between">
          <div className="flex flex-row gap-2 items-center">
            <p className="cs-caption m-0">{typeLabel}</p>
            {!scoreCardOnly && (
              <>
                <span className="cs-caption">|</span>
                <p className="cs-caption m-0">
                  Question {questionIndex + 1} of {totalQuestions}
                </p>
              </>
            )}
            <span className="cs-caption hidden lg:inline">|</span>
            <p className="cs-caption m-0 hidden lg:block">
              Released: {formatDate(updatedAt)}
            </p>
          </div>
          <div className="hidden lg:block">
            <CopyLink iconClassName="w-5 h-5 text-text-default-medium hover:text-text-default-strong" />
          </div>
        </div>
      </div>
    </div>
  );
}
